import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Loader2, ShieldAlert, Ban, Search, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = ['none', 'warned', 'restricted', 'review', 'suspended', 'removed'];
const LEVEL_COLOR = {
  none: 'bg-slate-100 text-slate-600',
  warned: 'bg-amber-100 text-amber-700',
  restricted: 'bg-orange-100 text-orange-700',
  review: 'bg-purple-100 text-purple-700',
  suspended: 'bg-rose-100 text-rose-700',
  removed: 'bg-red-200 text-red-800',
};

const FlaggedMessages = () => {
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [summary, setSummary] = useState({ open: 0, blocked: 0, warned: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');
  const [verdict, setVerdict] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        api.get('/admin/flagged-messages', { params: { status: status === 'all' ? '' : status, verdict, search: q } }),
        api.get('/admin/flagged-messages/summary'),
      ]);
      setFlags(f.data?.flags || []);
      setSummary(s.data || {});
    } catch (e) {
      toast.error('Failed to load flagged messages');
    } finally {
      setLoading(false);
    }
  }, [status, verdict, q]);

  useEffect(() => { load(); }, [status, verdict]); // eslint-disable-line

  const review = async (id, review_status) => {
    try {
      await api.post(`/admin/flagged-messages/${id}/review`, { review_status });
      toast.success(`Marked ${review_status}`);
      load();
    } catch (e) { toast.error('Failed to update'); }
  };

  const setLevel = async (userId, level) => {
    try {
      await api.post(`/admin/enforcement/${userId}`, { level });
      toast.success(`Enforcement set to ${level}`);
      load();
    } catch (e) { toast.error('Failed to set enforcement'); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-flagged-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="flagged-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-xl md:text-2xl font-extrabold flex items-center gap-2 text-slate-900">
            <ShieldAlert className="w-5 h-5 text-rose-500" /> Flagged Messages
          </h1>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3 flex gap-3 text-xs">
          <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 font-semibold" data-testid="flagged-open-count">{summary.open} open</span>
          <span className="px-2 py-1 rounded bg-rose-100 text-rose-700 font-semibold">{summary.blocked} blocked</span>
          <span className="px-2 py-1 rounded bg-amber-100 text-amber-700 font-semibold">{summary.warned} warned</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="flagged-status-filter"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="actioned">Actioned</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={verdict || 'all'} onValueChange={(v) => setVerdict(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="flagged-verdict-filter"><SelectValue placeholder="Verdict" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All verdicts</SelectItem>
              <SelectItem value="block">Blocked</SelectItem>
              <SelectItem value="warn">Warned</SelectItem>
            </SelectContent>
          </Select>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search content, client or talent…" className="pl-9" data-testid="flagged-search" />
            </div>
            <Button type="submit">Search</Button>
          </form>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
        ) : flags.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="flagged-empty">
            No flagged messages. The marketplace is clean 🎉
          </div>
        ) : (
          <div className="space-y-3" data-testid="flagged-list">
            {flags.map((f) => (
              <div key={f.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`flag-${f.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {f.verdict === 'block'
                      ? <Badge className="bg-rose-600 text-white border-0"><Ban className="w-3 h-3 mr-1" />Blocked</Badge>
                      : <Badge className="bg-amber-500 text-white border-0"><ShieldAlert className="w-3 h-3 mr-1" />Warned</Badge>}
                    <span className="text-xs text-slate-500">conf {(f.confidence * 100).toFixed(0)}% · {f.detector}</span>
                    {f.review_status !== 'open' && <Badge variant="outline" className="text-[10px]">{f.review_status}</Badge>}
                  </div>
                  <span className="text-[11px] text-slate-400">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</span>
                </div>

                <div className="mt-2 text-sm">
                  <span className="font-semibold text-slate-900">{f.client_name}</span>
                  <span className="text-slate-400 mx-1">→</span>
                  <span className="font-semibold text-slate-900">{f.talent_name}</span>
                  {f.gig_label && <span className="text-[11px] text-amber-700 ml-2">({f.gig_label})</span>}
                </div>

                <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm text-slate-800 whitespace-pre-wrap break-words" data-testid={`flag-content-${f.id}`}>
                  “{f.content}”
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(Array.isArray(f.categories) ? f.categories : []).map((c) => (
                    <span key={c} className="text-[10px] bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">{c}</span>
                  ))}
                </div>
                {f.reason && <p className="mt-1 text-xs text-slate-500 italic">{f.reason}</p>}

                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">Sender:</span>
                  <Badge className={`${LEVEL_COLOR[f.sender_enforcement_level] || ''} border-0 text-[10px]`}>
                    {f.sender_enforcement_level} · {f.sender_violation_count} violations
                  </Badge>
                  <div className="flex items-center gap-1.5 ml-auto flex-wrap">
                    <Select value={f.sender_enforcement_level} onValueChange={(v) => setLevel(f.sender_id, v)}>
                      <SelectTrigger className="w-36 h-8 text-xs" data-testid={`enforce-select-${f.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {f.review_status === 'open' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => review(f.id, 'dismissed')} data-testid={`dismiss-${f.id}`}>Dismiss</Button>
                        <Button size="sm" className="bg-slate-800 text-white hover:bg-slate-900" onClick={() => review(f.id, 'reviewed')} data-testid={`resolve-${f.id}`}>Mark reviewed</Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlaggedMessages;
